/* eslint-disable @typescript-eslint/naming-convention */
import { JobCard } from 'components/common/Job/JobCard';
import { JobCardLargeSize } from 'components/common/Job/JobCardLargeSize';
import { ApplyContext, ApplyStep } from 'context/apply.context';
import { ApplicationForm } from 'modules/application/ApplicationForm';
import { SuccessApplication } from 'modules/application/SuccessApplication';
import React, { useContext } from 'react';
import { JobType } from 'types';
import { formatDateRange } from 'utils/helper';



import { DLink, SvgUse } from '@dnse/ui';



import { JobDetailsStyle } from './JobDetailsStyle';


interface Props {
  job: JobType;
  recommendJobs: JobType[];
}

export const JobDetails: React.FC<Props> = ({ job, recommendJobs }) => {
  const {
    id,
    title,
    address,
    work_type,
    department,
    start_date,
    end_date,
    job_requirement,
    job_description,
    job_benefit
  } = job;
  const { step, setStep } = useContext(ApplyContext);

  const handleClickApplyButton = () => {
    setStep(ApplyStep.UploadCV);
    window.scrollTo(0, 0);
  };

  const handleGoBack = () => {
    setStep(step - 1);
  };

  return (
    <div className="flex justify-center scroll-smooth">
      <div className="w-full xl:w-[1120px] py-3">
        <div className="flex flex-col lg:flex-row">
          {/* aside in mobile */}
          {step === ApplyStep.ViewJobDetails && (
            <div className="lg:hidden flex flex-col gap-y-3 px-4 lg:px-0 text-sm font-normal mb-4">
              <h3
                className="text-xl font-semibold mb-1"
                data-testid="job-title"
              >
                {title}
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex text-gray-700 gap-x-1">
                  <SvgUse
                    id="calendar-outline"
                    width={16}
                    height={16}
                    className="mt-1"
                  />
                  Hạn nộp
                </div>
                <div className="font-medium">
                  {formatDateRange(start_date, end_date)}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex text-gray-700 gap-x-1">
                  <SvgUse
                    id="location-outline-gray"
                    width={16}
                    height={16}
                    className="mt-1"
                  />
                  Địa điểm
                </div>
                <div className="font-medium">
                  {address.length > 1
                    ? `${address[0]} - ${address[1]}`
                    : address[0]}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex text-gray-700 gap-x-1">
                  <SvgUse
                    id="dnse-time"
                    width={16}
                    height={16}
                    className="mt-1"
                  />
                  Hình thức làm việc
                </div>
                <div className="font-medium">{work_type}</div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex text-gray-700 gap-x-1">
                  <SvgUse
                    id="department-outline"
                    width={16}
                    height={16}
                    className="mt-1"
                  />
                  Phòng ban
                </div>
                <div className="font-medium">{department}</div>
              </div>
              <button
                data-testid="apply-job-button"
                onClick={handleClickApplyButton}
                className="mt-1 flex items-center justify-center text-brand-300 font-medium text-base px-6 py-[9px] h-[36px] rounded-[41px] border-[1px] border:solid border-brand-300"
              >
                Ứng tuyển
              </button>
            </div>
          )}

          {/* aside in web */}
          <aside className="w-full hidden lg:basis-1/4 lg:block mr-[32px]">
            {step !== ApplyStep.ViewJobDetails && (
              <button
                onClick={handleGoBack}
                className="cursor-pointer w-full flex items-center underline underline-offset-2 text-brand-300 py-[8px] text-base font-medium mb-3"
              >
                <SvgUse
                  id={'arrow-left'}
                  width={16}
                  height={16}
                  className="mr-2"
                />
                Quay lại
              </button>
            )}
            <div
              className="flex flex-col gap-y-4 px-3 py-4 shadow-sm rounded h-fit"
              style={{
                border: '1px solid  var(--light-line, rgba(0, 0, 0, 0.05))'
              }}
            >
              <div className="flex items-start gap-x-1">
                <SvgUse id="calendar-outline" width={16} height={16} />
                <div className="flex flex-col text-sm leading-4 gap-y-1">
                  <span className="font-normal text-gray-700">Hạn nộp</span>
                  <span className="font-medium">
                    {formatDateRange(start_date, end_date)}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-x-1 ">
                <SvgUse id="location-outline-gray" width={16} height={16} />
                <div className="flex flex-col text-sm leading-4 gap-y-1">
                  <span className="font-normal text-gray-700">Địa điểm</span>
                  <span className="font-medium">
                    {address.length > 1
                      ? `${address[0]} - ${address[1]}`
                      : address[0]}
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-x-1">
                <SvgUse id="dnse-time" width={16} height={16} />
                <div className="flex flex-col text-sm leading-4 gap-y-1">
                  <span className="font-normal text-gray-700">
                    Hình thức làm việc
                  </span>
                  <span className="font-medium">{work_type}</span>
                </div>
              </div>
              <div className="flex items-start gap-x-1">
                <SvgUse
                  id="department-outline"
                  width={16}
                  height={16}
                  className="text-gray-700"
                />
                <div className="flex flex-col text-sm leading-4 gap-y-1">
                  <span className="font-normal text-gray-700">Phòng ban</span>
                  <span className="font-medium">{department}</span>
                </div>
              </div>
            </div>
          </aside>

          {/* main content */}
          <article className="w-full lg:max-w-[823px] flex justify-center lg:block lg:justify-start">
            {step === ApplyStep.ViewJobDetails && (
              <div className="flex flex-col gap-y-[20px] w-full ">
                <div className="hidden lg:flex items-start">
                  <div className="md:basis-[75%]">
                    <h2
                      className="text-2xl font-medium"
                      data-testid="job-title"
                    >
                      {title}
                    </h2>
                  </div>
                  <div className="basis-1/3 md:flex justify-end">
                    <button
                      data-testid="apply-job-button"
                      onClick={handleClickApplyButton}
                      className="flex items-center text-brand-300 font-medium text-base px-6 py-[9px] h-[36px] rounded-[41px] border-[1px] border:solid border-brand-300"
                    >
                      Ứng tuyển
                    </button>
                  </div>
                </div>
                <JobDetailsStyle>
                  <div className="flex flex-col gap-y-[12px] px-4 lg:px-0">
                    <span className="text-brand-300 text-base font-semibold">
                      Mô tả công việc
                    </span>
                    <div
                      dangerouslySetInnerHTML={{ __html: job_description }}
                    />
                  </div>
                  <div className="flex flex-col gap-y-[12px] px-4 lg:px-0">
                    <span className="text-brand-300 text-base font-semibold">
                      Yêu cầu
                    </span>
                    <div
                      dangerouslySetInnerHTML={{ __html: job_requirement }}
                    />
                  </div>
                  <div className="flex flex-col gap-y-[12px] px-4 lg:px-0">
                    <span className="text-brand-300 text-base font-semibold">
                      Quyền lợi
                    </span>
                    <div dangerouslySetInnerHTML={{ __html: job_benefit }} />
                  </div>
                </JobDetailsStyle>
                <div
                  className="px-4 md:px-8 py-6 flex items-center w-screen md:w-full left-0"
                  style={{
                    background:
                      'linear-gradient(132deg, #FFE7E7 5.6%, #EDEFFF 35.43%, #EEFFFE 66.53%, #FFEEE2 100%)'
                  }}
                >
                  <div className="w-full flex flex-col md:flex-row justify-between items-center">
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-brand-300 text-center md:text-left">
                        Đã ưng ý thì gửi ngay CV nhé!
                      </div>
                      <div className="text-lg font-normal text-center md:text-left">
                        Bộ phận tuyển dụng của DNSE sẽ liên hệ{' '}
                        <br className="hidden md:block" /> với bạn sớm nhất nha!
                      </div>
                    </div>
                    <button
                      onClick={handleClickApplyButton}
                      className="mt-[12px] md:mt-0 bg-brand-300 rounded-[41px] w-full md:w-[200px] py-[14px] text-base font-medium flex items-center justify-center text-white"
                    >
                      <SvgUse
                        id="dnse-logo-white"
                        width={18}
                        height={18}
                        className="mr-1"
                      />
                      Nộp CV ngay
                    </button>
                  </div>
                </div>
                {recommendJobs.length > 0 && (
                  <div className="flex flex-col items-center px-4 lg:px-0 w-full md:min-w-[823px]">
                    <div className="flex items-center gap-2 mt-5 mb-7 w-full">
                      <div className="min-w-[3px] h-[35px] bg-brand-300 rounded-t-[100px]" />
                      <h3 className="text-base font-medium lg:text-lg lg:font-semibold">
                        Việc làm khác mà bạn có thể thích
                      </h3>
                    </div>
                    <div className="flex flex-col items-center gap-y-4">
                      <div className="flex-col gap-y-8 hidden md:flex">
                        {recommendJobs
                          .filter(recommendJob => recommendJob.id !== id)
                          .slice(0, 3)
                          .map(recommendJob => (
                            <JobCardLargeSize
                              key={recommendJob.id}
                              job={recommendJob}
                            />
                          ))}
                      </div>
                      <div className="flex flex-col gap-y-8 md:hidden">
                        {recommendJobs
                          .filter(recommendJob => recommendJob.id !== id)
                          .slice(0, 3)
                          .map(recommendJob => (
                            <JobCard key={recommendJob.id} job={recommendJob} />
                          ))}
                      </div>
                      <DLink
                        href="/co-hoi-viec-lam"
                        className="w-[200px] h-[38px] flex justify-center items-center text-brand-300 font-medium text-base px-[24px] py-[9px] rounded-[41px] border-[1px] border:solid border-brand-300 mb-[35px]"
                      >
                        Xem toàn bộ
                      </DLink>
                    </div>
                  </div>
                )}
              </div>
            )}
            {step === ApplyStep.UploadCV && (
              <ApplicationForm jobTitle={title} jobId={id} />
            )}
            {step === ApplyStep.SubmitSuccessfully && <SuccessApplication />}
          </article>
        </div>
      </div>
    </div>
  );
};
